/* eslint-disable */
// @ts-nocheck
import * as R from 'ramda';
import { STANDARD_EVENT_NAMES } from '../../../support/eo_mapping';
import {
  EO_COMMERCE_EVENT,
  EO_CUSTOM_EVENT,
  REVENUE,
  REVENUE_IN_LOCAL_CURRENCY,
} from '../../../support/eo_mapping/datasources';
import {
  getCustomEventName,
  isCustomEvent,
  dedupe,
  parseEventDescriptionFromEventKeyOrUndefined,
} from '../../../support/utils';
import type { SourceQuery, Query } from '../../../../main/types/druidTypes';
import InFilter from '../../../support/utils/druid/filters/InFilter';
import { updateFilter, OPERATORS, addFilter } from '../../../support/utils/druid/updateQuery';
import { getListOfDimensionsAndValues } from '../../../support/utils/druid/nativeQueries';
import type { Columns } from '../../../../main/types/tableTypes';
import AndFilter from '../../../support/utils/druid/filters/AndFilter';
import { TOTAL_COUNT } from '../../../../../../shared/constants/druidMetrics';
import { ContextsService } from '../../../../../../shared/Contexts.service';

/**
 * For tables and Timeseries, data_sources will already be an array of data sources.
 * @param {Array<string>} data_sources - All the data sources we want to query
 * druid for at one time.
 * For tables - this array will have various lengths:
 *   Ex: ['eo_click', 'eo_install', 'revenue']
 * For timeseries it will only have one item which will be an event name:
 *   Ex: ['CLICK'] or ['revenue']
 * @param {Array<string>} eventList
 * For tables - will be null
 * For timeseries - Array of the event names that appear in the 'Show' dropdown.
 *    Ex: ['CLICK', 'INSTALL', 'revenue']
 * @return {Object} Array<string> -
 * For tables - the same as the data_sources input as the first param
 * For timeseries - array containing one data source, no longer an event name.
 * Ex: ['eo_click'] or ['eo_commerce_event']
 */
export const getEventForTimeseries = (data_sources: string[], eventList: string[]) => {
  // Only timeseries will have an eventList (a dropdown of different event names)
  if (!eventList) {
    return null;
  }

  // return datasources for both table and timeseries use case
  return data_sources;
};

/**
 * method that either (1) adds a filter to or (2) replaces a filter for existingFilter.
 * The new filter will contain the (dimension, values) tuple and will always be an EQUALS filter.
 */
export const getSourceQueryFilter = (existingFilter: Record<string, any>, dimension: string, values: Array<string>) => {
  // case 1: existingFilter has no filter parameter
  if (!existingFilter) {
    return new InFilter(dimension, values);
  }

  const listOfDimensionsAndValues = getListOfDimensionsAndValues(existingFilter) || {};
  const existingDimensionsInFilters = Object.keys(listOfDimensionsAndValues);

  // case 2: existingFilter does not already specify a filter for dimension
  if (!existingDimensionsInFilters.includes(dimension)) {
    return addFilter(existingFilter, dimension, {}, values);
  }

  const existingValues = listOfDimensionsAndValues[dimension] || [];
  // case 3: existingFilter already specifies a filter for dimension
  // append new value with existing name value
  return updateFilter(
    existingFilter,
    {
      dimension,
      comparator: OPERATORS.EQUALS,
      nestingLevel: 0,
    },
    dimension,
    OPERATORS.EQUALS,
    values.concat(existingValues)
  );
};

/**
 * @param {string} dataSource - the data source to create the sourceQuery for.
 * Examples: 'eo_click', 'eo_install', 'eo_commerce_event'
 * @param {Object} existingSourceQuery - Object with keys for each data source
 * that requires a special source query
 * Ex: {
    eo_commerce_event: {
      filters: { name: ['123'] },
      ...
    },
    eo_install: { ... },
    ...
  }
 * @param {Object} newQueriesToAdd - Object with new values we want the
 * source query we're returning to have for the given data source.
 * Ex: {
    aggregations: ['revenue'],
    metric: 'revenue'
  }
 * @return {Object} the new source query which merges the existing source query
 * with the new query info for the data source.
 * Ex: {
    eo_commerce_event: {
      filters: { name: ['123'] },
      aggregations: ['revenue'],
      metric: 'revenue'
    },
    eo_install: { ... },
    ...
  }
 */
const _getUpdatedSourceQuery = (
  dataSource: string,
  existingSourceQuery: SourceQuery,
  newQueriesToAdd: Record<string, any>
) => {
  const existingDataSourceQuery = R.pathOr({}, [dataSource], existingSourceQuery);
  const updatedSourceQuery = R.assoc(dataSource, R.merge(existingDataSourceQuery, newQueriesToAdd), existingSourceQuery);
  return updatedSourceQuery || {};
};

/**
 * There are certain standard sourceQueries we always want for certain events.
 * This method will merge these standard sourceQueries with any existing
 * sourceQueries.
 * @param {string} event - the event name to add the source query for
 * Examples: 'revenue', 'PURCHASE', 'CLICK'
 * @param {Object} existingSourceQuery - Object with keys for each data source
 * that requires a special source query
 * Ex: {
    eo_commerce_event: {
      filters: { last_attributed_touch_data_tilde_id: ['abc'] },
      ...
    },
    eo_install: { ... },
    ...
  }
 * @return {Object} the new source query which merges the existing source query
 * with the known source queries needed for that event.
 * For Example: 'PURCHASE' always needs aggregations and metric in sourceQuery,
 * and a specific filter. This fn would therefore return: {
    eo_commerce_event: {
      filters: {
        name: ['PURCHASE', 'purchase'],
        last_attributed_touch_data_tilde_id: ['abc']
      },
      aggregations: ['revenue'],
      metric: 'revenue'
    },
    eo_install: { ... },
    ...
  }
 */
export const addStandardSourceQueryForEvent = (sourceQuery: SourceQuery, event: string) => {
  if (!!event) {
    if (event === REVENUE) {
      return _getUpdatedSourceQuery(EO_COMMERCE_EVENT, sourceQuery, {
        aggregations: dedupe([...(sourceQuery.eo_commerce_event.aggregations || []), REVENUE]),
        metric: REVENUE,
      });
    }

    if (event === REVENUE_IN_LOCAL_CURRENCY) {
      return _getUpdatedSourceQuery(EO_COMMERCE_EVENT, sourceQuery, {
        aggregations: dedupe([...(sourceQuery.eo_commerce_event.aggregations || []), REVENUE_IN_LOCAL_CURRENCY]),
        metric: REVENUE_IN_LOCAL_CURRENCY,
      });
    }

    if (event === 'PURCHASE') {
      return _getUpdatedSourceQuery(EO_COMMERCE_EVENT, sourceQuery, {
        filter: getSourceQueryFilter(sourceQuery.eo_commerce_event.filter, 'name', ['PURCHASE']),
        aggregations: dedupe([...(sourceQuery.eo_commerce_event.aggregations || []), TOTAL_COUNT]),
        metric: TOTAL_COUNT,
      });
    }

    if (STANDARD_EVENT_NAMES[event] || isCustomEvent(event)) {
      const dataSource = STANDARD_EVENT_NAMES[event] || EO_CUSTOM_EVENT;
      return _getUpdatedSourceQuery(dataSource, sourceQuery, {
        filter: getSourceQueryFilter(sourceQuery[dataSource].filter, 'name', [getCustomEventName(event)]),
      });
    }
  }

  return { ...sourceQuery };
};

/**
 * Use when we switch event selector to be a multi-select component for timeseries
 */
export const addStandardSourceQueryForEvents = (defaultSourceQuery: SourceQuery, events: string[]) => {
  if (!events || !events.length) {
    return defaultSourceQuery;
  }

  return events.reduce(addStandardSourceQueryForEvent, defaultSourceQuery);
};
export const getDefaultGranularityFromContext = (context: string) => {
  if (!context) {
    return 'all';
  }

  return ContextsService.isTableContext(context) ? 'all' : 'day';
};
export const DATE_RANGE_DIMENSION = 'date_range';
// [EG-2645] add/remove date_range as a dimension based on selected granularity
export const updateDimensionsWithDate = (granularity: string, dimensions: Array<string>) => {
  if (!granularity) {
    return dimensions;
  }

  if (granularity === 'all') {
    // remove date_range as a dimension
    return dimensions.filter((v) => v !== DATE_RANGE_DIMENSION);
  }

  if (dimensions.indexOf(DATE_RANGE_DIMENSION) === -1) {
    // add date_range as first dimension
    return [DATE_RANGE_DIMENSION, ...dimensions];
  }

  return dimensions;
};
export function generateAdditionalQueriesFromEvents(options: { query: Query; events: string[] }): Record<string, Query> {
  const { events, query } = options;
  const additionalQueries = events.reduce((acc, event) => {
    const eventDescription = parseEventDescriptionFromEventKeyOrUndefined(event);

    if (!eventDescription) {
      return acc;
    }

    const additionalQuery = {
      ...query,
      data_sources: [eventDescription.dataSource],
      dimensions: query.dimensions,
      filter: query.filter ? new AndFilter([eventDescription.filter, query.filter]) : eventDescription.filter,
    };
    return { ...acc, [event]: additionalQuery };
  }, {});
  return additionalQueries;
}
export function removeEventDescriptionsFromEvents(events: string[]): string[] {
  return events.filter((event) => {
    const eventDescription = parseEventDescriptionFromEventKeyOrUndefined(event);
    return !eventDescription;
  });
}
export function getNonDimensionColumnKeysFromSelectedColumns(selectedColumns: Columns | null | undefined): string[] {
  if (!selectedColumns) {
    return [];
  }

  return selectedColumns.filter((column) => column.type !== 'dimension').map((column) => column.key);
}
export function deriveOrganic(options: {
  disableOrganicToggle: boolean | null | undefined;
  shareableStateOrganic: boolean | null | undefined;
  initialOrganic: boolean;
  hasOrganicToggle: boolean | null | undefined;
}): boolean {
  const { disableOrganicToggle, hasOrganicToggle, shareableStateOrganic, initialOrganic } = options;

  if (disableOrganicToggle) {
    return false;
  }

  if (!hasOrganicToggle) {
    return false;
  }

  if (shareableStateOrganic == undefined) {
    return initialOrganic;
  }

  return shareableStateOrganic;
}

export function dimensionsAreNotAvailable(allAvailableDimensions: string[], selectedDimensions: string[]): boolean {
  return selectedDimensions
    .filter((selectedDimension) => selectedDimension !== 'date_range')
    .some((selectedDimension) => {
      return allAvailableDimensions.filter((availableDimension) => availableDimension === selectedDimension).length === 0;
    });
}
